import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useTranslation } from '../context/LanguageContext';

/**
 * SupervisorSelect - 直属上级选择器（按部门分组）
 *
 * Props:
 *   value     - 当前选中员工的 entity_id (string|number)
 *   onChange  - (employeeId: string) => void
 *   viewOnly  - boolean，只读模式
 *   excludeId - 需要排除的员工ID（编辑时排除自身）
 */
const SupervisorSelect = ({ value, onChange, viewOnly, excludeId }) => {
  const { t } = useTranslation();
  const [employees, setEmployees] = useState([]);
  const [empPositions, setEmpPositions] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [expandedDept, setExpandedDept] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    Promise.all([
      fetch('/api/employees').then(r => r.json()),
      fetch('/api/employees/positions').then(r => r.json()),
      fetch('/api/organization/departments').then(r => r.json()),
    ]).then(([empRes, posRes, deptRes]) => {
      if (empRes.status === 'success') setEmployees(empRes.data);
      if (posRes.status === 'success') setEmpPositions(posRes.data);
      if (deptRes.status === 'success') setDepartments(deptRes.data);
    }).catch(err => console.error('Failed to load data:', err));
  }, []);

  // 编辑时自动展开选中员工所在的部门
  useEffect(() => {
    if (!value || empPositions.length === 0) return;
    const pos = empPositions.find(ep => Number(ep.employee_id) === Number(value));
    if (pos && pos.department_id) {
      setExpandedDept(pos.department_id);
    }
  }, [value, empPositions]);

  // 点击外部关闭
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 按部门分组员工
  const deptEmployeeMap = {};
  departments.forEach(d => { deptEmployeeMap[d.id] = { ...d, employees: [] }; });
  deptEmployeeMap['unassigned'] = { id: 'unassigned', name: t('common.unassigned_dept'), employees: [] };

  empPositions.forEach(ep => {
    const emp = employees.find(e => Number(e.entity_id) === Number(ep.employee_id));
    if (!emp || (excludeId && Number(emp.entity_id) === Number(excludeId))) return;
    const deptId = ep.department_id || 'unassigned';
    if (!deptEmployeeMap[deptId]) {
      deptEmployeeMap[deptId] = { id: deptId, name: ep.department_name || t('common.other'), employees: [] };
    }
    const firstName = emp.attributes?.first_name || '';
    const lastName = emp.attributes?.last_name || '';
    deptEmployeeMap[deptId].employees.push({
      id: emp.entity_id,
      label: `${emp.entity_id} - ${firstName} - ${lastName}`,
    });
  });

  employees.forEach(emp => {
    if (excludeId && Number(emp.entity_id) === Number(excludeId)) return;
    const hasPosition = empPositions.some(ep => Number(ep.employee_id) === Number(emp.entity_id));
    if (!hasPosition) {
      const firstName = emp.attributes?.first_name || '';
      const lastName = emp.attributes?.last_name || '';
      deptEmployeeMap['unassigned'].employees.push({
        id: emp.entity_id,
        label: `${emp.entity_id} - ${firstName} - ${lastName}`,
      });
    }
  });

  const nonEmptyDepts = Object.values(deptEmployeeMap).filter(d => d.employees.length > 0);

  const handleSelect = (id) => {
    onChange(id);
    setDropdownOpen(false);
  };

  if (viewOnly) {
    const emp = employees.find(e => Number(e.entity_id) === Number(value));
    if (!emp) return <div className="w-full p-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-400">{t('common.not_set')}</div>;
    const firstName = emp.attributes?.first_name || '';
    const lastName = emp.attributes?.last_name || '';
    return (
      <div className="w-full p-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
        {emp.entity_id} - {firstName} - {lastName}
      </div>
    );
  }

  return (
    <div className="relative" ref={containerRef}>
      <div
        className="w-full p-2 border border-slate-300 dark:border-slate-700 rounded-lg bg-transparent cursor-pointer flex items-center gap-2 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
        onClick={() => setDropdownOpen(!dropdownOpen)}
      >
        {value ? (
          <span className="text-slate-700 dark:text-slate-300">
            {(() => {
              const emp = employees.find(e => Number(e.entity_id) === Number(value));
              if (!emp) return t('common.select_supervisor');
              const fn = emp.attributes?.first_name || '';
              const ln = emp.attributes?.last_name || '';
              return `${emp.entity_id} - ${fn} - ${ln}`;
            })()}
          </span>
        ) : (
          <span className="text-slate-400">{t('common.select_supervisor')}</span>
        )}
        <ChevronDown size={14} className="ml-auto text-slate-400" />
      </div>
      {dropdownOpen && (
        <div className="absolute left-0 right-0 z-50 mt-1 max-h-64 overflow-y-auto border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 shadow-lg">
          <div
            className="px-3 py-2 text-sm text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer border-b border-slate-100 dark:border-slate-800"
            onClick={() => handleSelect('')}
          >
            {t('common.no')}
          </div>
          {nonEmptyDepts.map(dept => (
            <div key={dept.id}>
              <div
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-800/50 cursor-pointer sticky top-0"
                onClick={() => setExpandedDept(expandedDept === dept.id ? null : dept.id)}
              >
                {expandedDept === dept.id ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                {dept.name}
                <span className="text-xs text-slate-400 ml-1">({dept.employees.length}{t('common.people')})</span>
              </div>
              {expandedDept === dept.id && dept.employees.map(emp => (
                <div
                  key={emp.id}
                  className={`px-3 py-1.5 text-sm cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/20 ${Number(value) === Number(emp.id) ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-medium' : 'text-slate-700 dark:text-slate-300'}`}
                  onClick={() => handleSelect(String(emp.id))}
                >
                  {emp.label}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SupervisorSelect;
